// ReportProblemModal — Angela 2026-05-19
// Inspector → Office "report a problem" flow with 2 mandatory photos for
// critical event types (deny_inspection, wrong_address, reschedule_cancel_issue,
// no_show, flipped_to_exterior_only).
//
// Workflow:
//   1. Inspector selects an event_type + writes a comment.
//   2. POST /api/inspector-field-events  →  event created with
//      workflow_status="pending_photos" for critical types.
//   3. Inspector uploads ERROR photo  →  POST /api/inspector-field-events/{id}/upload-photo
//   4. Inspector uploads CORRECT photo → same endpoint, kind=correct.
//   5. Inspector clicks "Submit to Office"  →  PATCH /finalize.
//      Backend rejects if either photo missing.
//   6. Office sees the event in /inspector-field-events/pending only AFTER
//      finalize (events stuck in pending_photos are hidden).
//
// The modal exposes a single button to cancel the half-created event so the
// office never sees orphaned pending_photos rows.

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  AlertTriangle, Camera, CheckCircle2, Loader2, X, Send, Upload, ArrowLeft,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROBLEM_TYPES = [
  {
    key: "deny_inspection",
    label: "Inspection Denied",
    short: "Homeowner refused the inspection",
    color: "border-[#C07070]/40 bg-[#C07070]/5 text-[#8B4545]",
  },
  {
    key: "wrong_address",
    label: "Wrong Address",
    short: "Address on the order doesn't match the location",
    color: "border-amber-300 bg-amber-50 text-amber-800",
  },
  {
    key: "no_show",
    label: "No Show",
    short: "No one was home / customer didn't appear",
    color: "border-orange-300 bg-orange-50 text-orange-800",
  },
  {
    key: "reschedule_cancel_issue",
    label: "Reschedule / Cancel Issue",
    short: "Customer wants to reschedule or cancel",
    color: "border-[#2F5D8A]/30 bg-[#2F5D8A]/5 text-[#2F5D8A]",
  },
  {
    key: "flipped_to_exterior_only",
    label: "Flipped to Exterior Only",
    short: "Inspection had to be limited to the exterior",
    color: "border-purple-300 bg-purple-50 text-purple-800",
  },
];

const STEP_TYPE = 1;
const STEP_PHOTOS = 2;

export default function ReportProblemModal({
  open,
  orderId,
  orderNumber,
  presetEventType,
  onClose,
  onSubmitted,
}) {
  const [step, setStep] = useState(STEP_TYPE);
  const [eventType, setEventType] = useState(presetEventType || "");
  const [comment, setComment] = useState("");
  const [creating, setCreating] = useState(false);
  const [eventId, setEventId] = useState(null);
  const [errorPhotoUrl, setErrorPhotoUrl] = useState("");
  const [correctPhotoUrl, setCorrectPhotoUrl] = useState("");
  const [uploading, setUploading] = useState({ error: false, correct: false });
  const [finalizing, setFinalizing] = useState(false);
  const [err, setErr] = useState("");
  const errorFileRef = useRef(null);
  const correctFileRef = useRef(null);

  // Reset on every open / orderId change
  useEffect(() => {
    if (open) {
      setStep(STEP_TYPE);
      setEventType(presetEventType || "");
      setComment("");
      setEventId(null);
      setErrorPhotoUrl("");
      setCorrectPhotoUrl("");
      setErr("");
      setCreating(false);
      setFinalizing(false);
    }
  }, [open, orderId, presetEventType]);

  const cancelAndClose = async () => {
    // If we have a pending_photos event, delete it server-side so the office
    // doesn't see orphaned rows.
    if (eventId) {
      try {
        await axios.delete(`${API}/inspector-field-events/${eventId}`);
      } catch (_) { /* best-effort */ }
    }
    onClose?.();
  };

  if (!open) return null;

  const proceedToPhotos = async () => {
    setErr("");
    if (!eventType) { setErr("Select a problem type."); return; }
    const c = (comment || "").trim();
    if (!c) { setErr("Describe the problem (minimum a few words)."); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/inspector-field-events`, {
        order_id: orderId,
        event_type: eventType,
        comment: c,
      });
      setEventId(res.data?.event?.id);
      setStep(STEP_PHOTOS);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create report.");
    } finally {
      setCreating(false);
    }
  };

  const uploadPhoto = async (kind, file) => {
    if (!file || !eventId) return;
    setErr("");
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const res = await axios.post(
        `${API}/inspector-field-events/${eventId}/upload-photo`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (kind === "error") setErrorPhotoUrl(res.data.url);
      else setCorrectPhotoUrl(res.data.url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const finalize = async () => {
    setErr("");
    if (!errorPhotoUrl || !correctPhotoUrl) {
      setErr("Both photos (error + correct) are required.");
      return;
    }
    setFinalizing(true);
    try {
      await axios.patch(`${API}/inspector-field-events/${eventId}/finalize`);
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to submit to Office.");
    } finally {
      setFinalizing(false);
    }
  };

  const selectedTypeMeta = PROBLEM_TYPES.find((t) => t.key === eventType);
  const isBusy = creating || finalizing || uploading.error || uploading.correct;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[1100] flex items-center justify-center p-3"
      data-testid="report-problem-modal-backdrop"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="report-problem-modal"
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {step === STEP_PHOTOS && (
              <button
                type="button"
                onClick={() => setStep(STEP_TYPE)}
                disabled={isBusy}
                className="text-gray-400 hover:text-gray-700"
                data-testid="report-problem-back"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <AlertTriangle className="h-4 w-4 text-[#C07070] shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-800 truncate">
                Report Problem
              </p>
              {orderNumber && (
                <p className="text-[11px] text-gray-500 truncate">
                  Order #{orderNumber}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={cancelAndClose}
            disabled={isBusy}
            className="text-gray-400 hover:text-gray-800"
            data-testid="report-problem-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === STEP_TYPE && (
          <div className="p-4 space-y-3" data-testid="report-problem-step-type">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
              What's the problem? <span className="text-red-500">*</span>
            </p>
            <div className="space-y-1.5">
              {PROBLEM_TYPES.map((t) => (
                <label
                  key={t.key}
                  className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    eventType === t.key
                      ? "border-[#2F5D8A] bg-[#2F5D8A]/5"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  data-testid={`report-problem-type-${t.key}`}
                >
                  <input
                    type="radio"
                    name="problem-type"
                    value={t.key}
                    checked={eventType === t.key}
                    onChange={() => setEventType(t.key)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{t.label}</p>
                    <p className="text-[11px] text-gray-500">{t.short}</p>
                  </div>
                </label>
              ))}
            </div>

            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mt-3">
              Describe what happened <span className="text-red-500">*</span>
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="Tell the office exactly what happened, what you saw, and any details they need to know."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#2F5D8A]"
              data-testid="report-problem-comment"
            />

            {err && (
              <p className="text-xs text-[#8B4545] bg-[#C07070]/10 border border-[#C07070]/30 rounded px-2 py-1.5" data-testid="report-problem-error">
                {err}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancelAndClose}
                disabled={isBusy}
                className="px-3 py-2 text-xs text-gray-500 hover:text-gray-800"
                data-testid="report-problem-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={proceedToPhotos}
                disabled={isBusy}
                className="px-4 py-2 bg-[#2F5D8A] hover:bg-[#264A6E] text-white text-xs font-semibold rounded-lg flex items-center gap-2 disabled:opacity-40"
                data-testid="report-problem-next"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                Next — Add Photos
              </button>
            </div>
            <p className="text-[10px] text-gray-400 leading-snug pt-1">
              Critical problems require 2 photos (error + correct) before the office sees them.
            </p>
          </div>
        )}

        {step === STEP_PHOTOS && (
          <div className="p-4 space-y-3" data-testid="report-problem-step-photos">
            {selectedTypeMeta && (
              <div className={`px-2.5 py-1.5 rounded-lg border text-[11px] font-bold ${selectedTypeMeta.color}`} data-testid="report-problem-type-banner">
                {selectedTypeMeta.label}
              </div>
            )}
            <p className="text-[11px] text-gray-600 leading-snug">
              Upload 2 photos so the office can verify the problem. Both are mandatory.
            </p>

            <div className="grid grid-cols-1 gap-3">
              <PhotoSlot
                kind="error"
                label="Photo of the PROBLEM"
                helper="Show what went wrong (e.g. the wrong door, the locked gate, the missing roof access)."
                url={errorPhotoUrl}
                uploading={uploading.error}
                fileRef={errorFileRef}
                onPick={(f) => uploadPhoto("error", f)}
              />
              <PhotoSlot
                kind="correct"
                label="Photo of the CONTEXT / FIX"
                helper="Show the surrounding context, the GPS, the actual address sign, or whatever proves the situation."
                url={correctPhotoUrl}
                uploading={uploading.correct}
                fileRef={correctFileRef}
                onPick={(f) => uploadPhoto("correct", f)}
              />
            </div>

            {err && (
              <p className="text-xs text-[#8B4545] bg-[#C07070]/10 border border-[#C07070]/30 rounded px-2 py-1.5" data-testid="report-problem-error">
                {err}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancelAndClose}
                disabled={isBusy}
                className="px-3 py-2 text-xs text-gray-500 hover:text-gray-800"
                data-testid="report-problem-cancel-step2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={finalize}
                disabled={!errorPhotoUrl || !correctPhotoUrl || isBusy}
                className="px-4 py-2 bg-[#C07070] hover:bg-[#8B4545] text-white text-xs font-semibold rounded-lg flex items-center gap-2 disabled:opacity-40"
                data-testid="report-problem-submit"
              >
                {finalizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Submit to Office
              </button>
            </div>
            <p className="text-[10px] text-gray-400 leading-snug">
              The office will receive a notification once both photos are uploaded and you submit.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function PhotoSlot({ kind, label, helper, url, uploading, fileRef, onPick }) {
  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-3" data-testid={`photo-slot-${kind}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">{label}</p>
      <p className="text-[10px] text-gray-500 mb-2 leading-snug">{helper}</p>
      {url ? (
        <div className="space-y-2">
          <div className="relative rounded-lg overflow-hidden border border-gray-200">
            <img
              src={`${BACKEND_URL}${url}`}
              alt={label}
              className="w-full h-32 object-cover"
              data-testid={`photo-preview-${kind}`}
            />
            <div className="absolute top-2 right-2 bg-[#4A7A56] text-white rounded-full p-1 shadow">
              <CheckCircle2 className="h-3.5 w-3.5" />
            </div>
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full text-[10px] text-gray-500 hover:text-gray-700 underline"
            data-testid={`photo-replace-${kind}`}
          >
            Replace photo
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full px-3 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-lg text-xs text-gray-600 flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid={`photo-upload-${kind}`}
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {uploading ? "Uploading..." : "Take / Choose Photo"}
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid={`photo-input-${kind}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
